"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import { getLanguageFromPathname } from "@/lib/i18n/paths";
import { createTranslator } from "@/lib/i18n/translations";
import { logError } from "@/lib/logger";

interface ErrorViewProps {
  error: Error & { digest?: string };
  reset: () => void;
  /** Identifies the boundary in logs */
  boundary: string;
}

/** Shared UI for route error boundaries. */
export function ErrorView({ error, reset, boundary }: ErrorViewProps) {
  const lang = getLanguageFromPathname(usePathname());
  const t = createTranslator(lang);

  useEffect(() => {
    logError("Unhandled application error", error, {
      component: boundary,
      ...(error.digest && { digest: error.digest }),
    });
  }, [error, boundary]);

  return (
    <section
      className="mx-auto flex min-h-[50vh] max-w-xl flex-col items-center justify-center gap-3 py-8 text-center sm:min-h-[60vh] sm:gap-4 sm:py-16"
      role="alert"
    >
      <p aria-hidden className="text-5xl sm:text-6xl">
        🎬
      </p>
      <h1 className="text-2xl font-bold sm:text-3xl">
        {t("errors.errorTitle")}
      </h1>
      <p className="text-default-500">{t("errors.errorDescription")}</p>
      {error.digest && (
        <p className="font-mono text-xs text-default-400">
          {t("errors.errorReference", { digest: error.digest })}
        </p>
      )}
      <div className="mt-2 flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:justify-center">
        <button
          className="min-h-11 rounded-full bg-success px-5 py-2.5 text-center font-semibold text-success-foreground transition-opacity hover:opacity-90"
          type="button"
          onClick={reset}
        >
          {t("errors.tryAgain")}
        </button>
        <a
          className="min-h-11 rounded-full bg-default-100 px-5 py-2.5 text-center font-semibold transition-colors hover:bg-default-200"
          href={`/${lang}`}
        >
          {t("errors.goHome")}
        </a>
      </div>
    </section>
  );
}
